import type { Column } from './types';

export const DEFAULT_COLUMN_WIDTH = 165;

export const getNewlySelectedId = (
  previous: readonly string[],
  next: readonly string[],
): string | undefined => {
  const previousIds = new Set(previous);
  const added = next.filter((id) => !previousIds.has(id));
  return added[0];
};

export const getColumnWidth = <T>(
  column: Column<T>,
  colWidths: Record<string, number>,
) =>
  colWidths[column.id] ||
  (typeof column.width === 'number' ? column.width : DEFAULT_COLUMN_WIDTH);

export const getColumnResizeMinWidth = <T>(
  column: Column<T> | undefined,
  floor: number,
) => Math.min(floor, typeof column?.width === 'number' ? column.width : floor);

export const groupStickyColumnsByEdge = <T>(columns: Column<T>[]) => [
  ...columns.filter((c) => c.sticky === 'left'),
  ...columns.filter((c) => !c.sticky),
  ...columns.filter((c) => c.sticky === 'right'),
];

const isReorderable = <T>(column: Column<T>) =>
  !column.metaColumn && !column.sticky;

export const applySavedColumnOrder = <T>(
  columns: Column<T>[],
  savedOrder?: string[],
) => {
  if (!savedOrder?.length) return groupStickyColumnsByEdge(columns);

  const remaining = columns.filter(isReorderable);
  const ordered: Column<T>[] = [];

  savedOrder.forEach((columnId) => {
    const index = remaining.findIndex((c) => c.id === columnId);
    if (index !== -1) ordered.push(...remaining.splice(index, 1));
  });

  return groupStickyColumnsByEdge([
    ...ordered,
    ...remaining,
    ...columns.filter((c) => !isReorderable(c)),
  ]);
};
